import React, { useEffect } from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer, Breadcrumbs } from "@/components/Navigation";
import {
  ArrowRight,
  BookOpen,
  Calendar,
  CheckCircle2,
  Clock3,
  Footprints,
  Phone,
  Sparkles,
  Stethoscope
} from "lucide-react";

const DEARDOC_BLOG_SCRIPT =
  "https://firebasestorage.googleapis.com/v0/b/deardoc-embed-codes.appspot.com/o/Wasatch%20Foot%20%26%20Ankle%20Center%2FWasatch%20Foot%20%26%20Ankle%20Center%20-%20Spence%20Harper%2Fcode.js?alt=media&token=9cd16a88-9981-4732-a81a-daf81c51f63b";

const topicLinks = [
  { label: "Plantar warts", href: "/services/swift-microwave-therapy/" },
  { label: "Heel pain", href: "/services/heel-pain-plantar-fasciitis/" },
  { label: "Bunions", href: "/services/bunion-correction/" },
  { label: "Foot surgery", href: "/services/surgical-foot-ankle-corrections/" }
];

export default function Articles() {
  useEffect(() => {
    // The static script below covers direct visits. This fallback covers client-side navigation.
    if (!document.getElementById("deardoc-articles-script")) {
      const script = document.createElement("script");
      script.id = "deardoc-articles-script";
      script.type = "text/javascript";
      script.src = DEARDOC_BLOG_SCRIPT;
      document.body.appendChild(script);
    }
  }, []);

  const articlesSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Foot & Ankle Articles | Wasatch Foot & Ankle Center",
    url: "https://www.drspenceharper.com/articles/",
    description:
      "Patient education articles and clinical insights from Dr. Spence Harper, DPM, in Heber City, Utah."
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f7f7] text-[#102d47]">
      <SEOHead
        title="Foot & Ankle Articles | Wasatch Foot & Ankle Center"
        description="Read foot and ankle health articles from Dr. Spence Harper, DPM, including guidance on plantar warts, heel pain, bunions, and treatment options in Heber City, Utah."
        canonicalPath="/articles/"
        schema={articlesSchema}
      />
      <Header />
      <Breadcrumbs items={[{ label: "Articles" }]} />

      <main className="flex-1">
        {/* Editorial hero */}
        <section className="relative overflow-hidden bg-[#dfecef] border-b border-[#c7dadd]">
          <div className="absolute -right-24 -top-32 h-80 w-80 rounded-full border-[28px] border-white/45" />
          <div className="absolute right-32 -bottom-44 h-80 w-80 rounded-full bg-[#b9d9dc]/45 blur-2xl" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8 space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/75 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0b7897] shadow-sm border border-white">
                  <BookOpen className="w-4 h-4" /> The Wasatch Journal
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.03] tracking-tight text-[#102d47] max-w-3xl">
                  Better foot health starts with better information.
                </h1>
                <p className="max-w-2xl text-base sm:text-lg leading-relaxed text-[#36546c] font-body">
                  Practical explanations, treatment guidance, and clinical perspective from Wasatch Foot &amp; Ankle Center in Heber City. Explore the topics patients ask about most.
                </p>
                <div className="flex flex-wrap gap-2.5 pt-1">
                  {topicLinks.map((topic) => (
                    <Link
                      key={topic.href}
                      href={topic.href}
                      className="group inline-flex items-center gap-2 rounded-full bg-white/85 px-4 py-2 text-sm font-semibold text-[#17425f] border border-white shadow-sm hover:-translate-y-0.5 hover:bg-white transition-all"
                    >
                      {topic.label}
                      <ArrowRight className="w-3.5 h-3.5 text-[#0284c7] transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-4 lg:justify-self-end w-full max-w-sm">
                <div className="relative rounded-[2rem] bg-[#123b59] p-7 text-white shadow-2xl overflow-hidden">
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full border border-cyan-300/30" />
                  <div className="absolute -right-1 -top-1 h-16 w-16 rounded-full bg-cyan-400/10" />
                  <div className="relative space-y-5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-400/15 text-cyan-200 border border-cyan-300/25">
                        <Stethoscope className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-cyan-200">A note from the clinic</p>
                        <p className="font-heading text-lg font-bold">Clear answers. Thoughtful care.</p>
                      </div>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-200">
                      “The right treatment begins with understanding what is happening — and why. Our articles are designed to make your next conversation with Dr. Harper more useful.”
                    </p>
                    <div className="flex items-center gap-3 border-t border-white/15 pt-4 text-xs text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-cyan-300" />
                      Board-certified podiatric care
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Article feed */}
        <section className="py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-7">
              <div>
                <div className="flex items-center gap-2 text-[#0284c7] text-xs uppercase tracking-[0.16em] font-bold mb-2">
                  <Sparkles className="w-4 h-4" /> From the practice
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#102d47]">Latest articles</h2>
                <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-2xl">
                  Browse the DearDoc article library for accessible, patient-first guidance on common foot and ankle concerns.
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-3.5 py-2 shadow-sm">
                <Clock3 className="w-4 h-4 text-[#0284c7]" /> Read at your own pace
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-8 items-start">
              <div className="rounded-[2rem] bg-white border border-slate-200 shadow-[0_18px_50px_rgba(15,43,72,0.08)] overflow-hidden">
                <div className="border-b border-slate-100 bg-[#fbfcfc] px-6 sm:px-8 py-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#dff4f5] flex items-center justify-center text-[#0284c7]">
                      <Footprints className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#102d47]">Patient education library</p>
                      <p className="text-xs text-slate-500">Updated by Wasatch Foot &amp; Ankle Center</p>
                    </div>
                  </div>
                  <span className="hidden sm:inline-flex text-[11px] uppercase tracking-widest font-bold text-[#0284c7]">Articles</span>
                </div>

                <div className="p-5 sm:p-8 bg-[linear-gradient(135deg,#ffffff_0%,#f7fbfb_100%)]">
                  {/* DearDoc BlogEngine container */}
                  <div id="deardoc-blog" data-blog-id="9f7d52b5-8cd7-4f10-ad78-599e89789751" className="min-h-[260px]">
                    <div id="dib-posts"></div>
                  </div>
                </div>
              </div>

              <aside className="space-y-5">
                <div className="rounded-[1.5rem] bg-[#123b59] p-6 text-white shadow-lg">
                  <p className="text-xs uppercase tracking-[0.16em] font-bold text-cyan-200">Need a diagnosis?</p>
                  <h3 className="font-heading text-2xl font-bold mt-2 leading-tight">Bring your questions to the office.</h3>
                  <p className="text-sm text-slate-200 leading-relaxed mt-3">
                    Articles can help you prepare, but a hands-on evaluation is the best next step for persistent pain or a changing condition.
                  </p>
                  <Link
                    href="/appointments/"
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0284c7] hover:bg-[#0b9bd0] px-4 py-3 text-sm font-bold text-white transition-all"
                  >
                    <Calendar className="w-4 h-4" /> Request an appointment
                  </Link>
                </div>

                <div className="rounded-[1.5rem] bg-white border border-slate-200 p-6 shadow-sm">
                  <h3 className="font-heading text-lg font-bold text-[#102d47]">Explore by concern</h3>
                  <div className="mt-4 space-y-2">
                    {topicLinks.map((topic) => (
                      <Link
                        key={topic.href}
                        href={topic.href}
                        className="group flex items-center justify-between rounded-xl bg-slate-50 hover:bg-[#e9f6f7] px-3.5 py-3 text-sm font-semibold text-slate-700 transition-colors"
                      >
                        {topic.label}
                        <ArrowRight className="w-4 h-4 text-[#0284c7] transition-transform group-hover:translate-x-1" />
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="rounded-[1.5rem] border border-[#c9e4e6] bg-[#e8f5f5] p-6">
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl bg-white p-2 text-[#0284c7] shadow-sm">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#0b7897]">Prefer to talk?</p>
                      <a href="tel:435-657-0329" className="mt-1 block font-heading text-xl font-bold text-[#102d47] hover:text-[#0284c7]">
                        (435) 657-0329
                      </a>
                      <p className="mt-1 text-xs leading-relaxed text-slate-600">Our Heber City team can help you choose the right next step.</p>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
